import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import { useState,useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FiMic, FiSearch, FiSun, FiMoon, FiX, FiUser,
  FiCompass, FiFilm, FiCalendar, FiTv, FiPlayCircle, FiZap, FiAperture, FiInfo,
} from 'react-icons/fi';
import axios from 'axios';
import Api_key from './apiconfiguration';
import { useAuth } from './AuthContext';
import navigateWithTransition from './navigateWithTransition';

const NavBar = ({setSearch, search, theme, toggleTheme}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchvalue, getvalue] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [listening, setListening] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const cinematic = /^\/(movie|tv)\//.test(location.pathname) || (!search && ['/', '/series'].includes(location.pathname));
  const { user, openAuth } = useAuth();
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  const startVoiceSearch = () => {
    if (!SpeechRecognition || listening) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognition.onresult = (event) => {
      const value = event.results?.[0]?.[0]?.transcript?.trim();
      if (!value) return;
      getvalue(value);
      setSearch(value);
      setSuggestionsOpen(false);
      setExpanded(false);
      navigate('/');
    };
    recognition.start();
  };

  const accountControl = (mobile = false) => user ? (
    <NavLink
      to="/me"
      className={`${mobile ? "icon-button" : "account-button desktop-account"}`}
      aria-label="Open your personal space"
      title="My space"
      onClick={() => setExpanded(false)}
    >
      {user.photoURL ? <img src={user.photoURL} alt="" referrerPolicy="no-referrer" /> : <FiUser />}
      {!mobile && <span>My space</span>}
    </NavLink>
  ) : (
    <button
      type="button"
      className={mobile ? "icon-button" : "account-button desktop-account"}
      onClick={openAuth}
      aria-label="Sign in with Google"
    >
      <FiUser />{!mobile && <span>Sign in</span>}
    </button>
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = searchvalue.trim();
    if (!value) return;
    setSearch(value);
    setSuggestionsOpen(false);
    setExpanded(false);
    navigate('/');
  };

  const clearSearch = () => {
    getvalue('');
    setSearch('');
    setSuggestions([]);
    setSuggestionsOpen(false);
  };

  const closeNavigation = () => {
    setExpanded(false);
    setSearch('');
    getvalue('');
  };
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => setExpanded(false), [location.pathname]);

  // The mobile tab bar has no search field of its own; it asks the header for one.
  useEffect(() => {
    const openSearch = () => {
      setExpanded(true);
      setTimeout(() => document.querySelector('.search-form input')?.focus(), 60);
    };
    window.addEventListener('hedone:open-search', openSearch);
    return () => window.removeEventListener('hedone:open-search', openSearch);
  }, []);

  // "/" jumps to search from anywhere, like most media sites.
  useEffect(() => {
    const focusSearch = event => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      event.preventDefault();
      const input = document.querySelector('.search-form input');
      if (input?.offsetParent) input.focus();
      else window.dispatchEvent(new CustomEvent('hedone:open-search'));
    };
    document.addEventListener('keydown', focusSearch);
    return () => document.removeEventListener('keydown', focusSearch);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setExpanded(false);
        document.querySelector('.navbar-toggler')?.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [expanded]);

  useEffect(() => {
    const query = searchvalue.trim();
    if (query.length < 2) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      setSuggestionsLoading(false);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSuggestionsLoading(true);
      try {
        const { data } = await axios.get('https://api.themoviedb.org/3/search/multi', {
          params: { api_key: Api_key, query, language: 'en-US', page: 1 },
          signal: controller.signal,
        });
        const results = (data.results || [])
          .filter(item => ['movie', 'tv'].includes(item.media_type) && (item.poster_path || item.backdrop_path))
          .slice(0, 6);
        setSuggestions(results);
        setSuggestionsOpen(true);
        setActiveSuggestion(-1);
      } catch (error) {
        if (error.name !== 'CanceledError') setSuggestions([]);
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300);

    return () => { clearTimeout(timer); controller.abort(); };
  }, [searchvalue]);

  const chooseSuggestion = (item) => {
    getvalue(item.title || item.name);
    setSearch('');
    setSuggestionsOpen(false);
    setExpanded(false);
    navigateWithTransition(navigate, `/${item.media_type}/${item.id}`);
  };

  const handleSearchKeys = (event) => {
    if (!suggestionsOpen || !suggestions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestion(index => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestion(index => index <= 0 ? suggestions.length - 1 : index - 1);
    } else if (event.key === 'Enter' && activeSuggestion >= 0) {
      event.preventDefault();
      chooseSuggestion(suggestions[activeSuggestion]);
    } else if (event.key === 'Escape') {
      setSuggestionsOpen(false);
    }
  };

  // Icons only show in the collapsed menu, where rows need a visual anchor.
  const links = [
    ['/', 'Discover', FiCompass],
    ['/air', 'In theaters', FiFilm],
    ['/upcoming', 'Coming soon', FiCalendar],
    ['/series', 'Series', FiTv],
    ['/shorts', 'Shorts', FiPlayCircle],
    ['/hunch', 'Hunch', FiZap],
    ['/orbit', 'Orbit', FiAperture],
    ['/dev', 'About', FiInfo],
  ];

  return (
    <header className={`navb ${cinematic ? 'nav-cinematic' : ''} ${isScrolled || expanded ? "scrolled" : ""}`}>
      {expanded && <div className="nav-scrim" onClick={() => setExpanded(false)} aria-hidden="true" />}
      <Navbar expand="lg" expanded={expanded}>
        <Container fluid className="nav-shell">
        <Navbar.Brand as={NavLink} to="/" onClick={closeNavigation} className="brand">
          <img src="/hedone.png" alt="Hedone" className="brand-logo" /><span>HEDONE</span>
        </Navbar.Brand>
        <div className="nav-mobile-actions">
          {accountControl(true)}
          <button className="icon-button" onClick={toggleTheme} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}>
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          <Navbar.Toggle aria-controls="navbarMenu" onClick={() => setExpanded(!expanded)}>
          <span className="custom-toggler-icon">
            <span></span>
            <span></span>
            <span></span>
          </span>
          </Navbar.Toggle>
        </div>
        <Navbar.Collapse id="navbarMenu">
          <nav className="nav-links" aria-label="Main navigation">
            {links.map(([to, label, Icon]) => (
              <NavLink key={to} to={to} end={to === '/'} onClick={closeNavigation} className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}>
                <Icon aria-hidden="true" /><span>{label}</span>
              </NavLink>
            ))}
          </nav>
          <form className={`search-form ${SpeechRecognition ? 'has-voice' : ''}`} onSubmit={handleSubmit} role="search">
            <FiSearch className="search-icon" />
            <input
              type="search"
              placeholder="Search titles"
              aria-label="Search movies and series"
              role="combobox"
              aria-autocomplete="list"
              autoComplete="off"
              aria-expanded={suggestionsOpen}
              aria-controls="search-suggestions"
              aria-activedescendant={suggestionsOpen && activeSuggestion >= 0 ? `search-option-${activeSuggestion}` : undefined}
              value={searchvalue}
              onChange={(e)=>getvalue(e.target.value)}
              onFocus={() => suggestions.length && setSuggestionsOpen(true)}
              onBlur={() => setTimeout(() => setSuggestionsOpen(false), 150)}
              onKeyDown={handleSearchKeys}
            />
            {!searchvalue && <kbd className="search-kbd" aria-hidden="true">/</kbd>}
            {searchvalue && <button type="button" className="clear-search" onClick={clearSearch} aria-label="Clear search"><FiX /></button>}
            {SpeechRecognition && <button type="button" className={`voice-search ${listening ? 'is-listening' : ''}`} onClick={startVoiceSearch} aria-label={listening ? 'Listening for a title' : 'Search by voice'} title="Voice search"><FiMic /></button>}
            {(suggestionsOpen || suggestionsLoading) && (
              <div className="search-suggestions" id="search-suggestions" role="listbox">
                {suggestionsLoading && !suggestions.length ? (
                  <div className="suggestion-status"><span className="suggestion-spinner" /> Finding titles…</div>
                ) : suggestions.map((item, index) => (
                  <button
                    type="button"
                    role="option"
                    id={`search-option-${index}`}
                    aria-selected={index === activeSuggestion}
                    key={`${item.media_type}-${item.id}`}
                    className={`suggestion-item ${index === activeSuggestion ? 'active' : ''}`}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => chooseSuggestion(item)}
                    onMouseEnter={() => setActiveSuggestion(index)}
                  >
                    <img src={`https://image.tmdb.org/t/p/w92${item.poster_path || item.backdrop_path}`} alt="" />
                    <span><strong>{item.title || item.name}</strong><small>{item.media_type === 'movie' ? 'Movie' : 'Series'} · {(item.release_date || item.first_air_date || '').slice(0, 4) || 'TBA'}</small></span>
                    {item.vote_average > 0 && <em>{item.vote_average.toFixed(1)}</em>}
                  </button>
                ))}
              </div>
            )}
          </form>
          <button className="icon-button desktop-theme" onClick={toggleTheme} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} title="Toggle theme">
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          {accountControl()}
        </Navbar.Collapse>
      </Container>
    </Navbar>
    </header>
  );
}

export default NavBar;
